/**
 * AdminShell - Layout wrapper for all admin portal pages
 * Sidebar with RBAC-aware navigation, header, responsive
 */

import React, { useState } from 'react';
import { navigate, adminLogout, getAdminData, hasPermission } from './api';

const ALL_NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: '📊', always: true },
  { path: '/admin/prospects', label: 'Prospects', icon: '🎯', resource: 'prospects', action: 'view' },
  { path: '/admin/clients', label: 'Clients', icon: '🏢', resource: 'clients', action: 'view' },
  { path: '/admin/tickets', label: 'Tickets', icon: '🎫', resource: 'tickets', action: 'view' },
  { path: '/admin/invoices', label: 'Invoices', icon: '💰', resource: 'invoices', action: 'view' },
  { path: '/admin/services', label: 'Services', icon: '⚡', resource: 'services', action: 'view' },
  { path: '/admin/documents', label: 'Documents', icon: '📄', resource: 'documents', action: 'view' },
  { path: '/admin/vendors', label: 'Vendors', icon: '🤝', resource: 'vendors', action: 'view' },
  { path: '/admin/activity', label: 'Activity', icon: '📋', resource: 'audit', action: 'view' },
  { path: '/admin/users', label: 'Team', icon: '👥', resource: 'users', action: 'view' },
  { path: '/admin/settings', label: 'Settings', icon: '⚙️', resource: 'settings', action: 'view' },
];

export default function AdminShell({ children, title }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const admin = getAdminData();
  const currentPath = window.location.pathname;

  // Filter nav items by permission
  const navItems = ALL_NAV_ITEMS.filter(item =>
    item.always || hasPermission(item.resource, item.action)
  );

  const roleColors = {
    owner: '#C084FC',
    director: '#3B82F6',
    manager: '#10B981',
    agent: '#F59E0B',
    viewer: '#6B7280',
  };

  const s = {
    wrapper: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: '#0A0F1D',
      color: '#FFFFFF',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    sidebar: {
      width: sidebarOpen ? 260 : 64,
      backgroundColor: '#0F1729',
      borderRight: '1px solid #1A2332',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s ease',
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: 100,
    },
    logo: {
      padding: '20px 16px',
      borderBottom: '1px solid #1A2332',
      fontSize: sidebarOpen ? 16 : 12,
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    nav: { flex: 1, padding: '12px 8px', overflowY: 'auto' },
    navItem: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 12px',
      borderRadius: 8,
      marginBottom: 2,
      cursor: 'pointer',
      fontSize: 14,
      color: active ? '#FFFFFF' : '#8B92A5',
      backgroundColor: active ? '#1E293B' : 'transparent',
      transition: 'all 0.15s ease',
    }),
    navIcon: { fontSize: 17, width: 24, textAlign: 'center', flexShrink: 0 },
    navLabel: { display: sidebarOpen ? 'block' : 'none', whiteSpace: 'nowrap' },
    main: {
      flex: 1,
      marginLeft: sidebarOpen ? 260 : 64,
      transition: 'margin-left 0.2s ease',
    },
    header: {
      padding: '16px 32px',
      borderBottom: '1px solid #1A2332',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#0F1729',
    },
    headerTitle: { fontSize: 20, fontWeight: 600 },
    headerRight: { display: 'flex', alignItems: 'center', gap: 16 },
    roleBadge: {
      padding: '3px 10px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      backgroundColor: `${roleColors[admin?.role] || '#6B7280'}20`,
      color: roleColors[admin?.role] || '#6B7280',
    },
    logoutBtn: {
      padding: '8px 16px',
      borderRadius: 6,
      border: '1px solid #2A3444',
      backgroundColor: 'transparent',
      color: '#8B92A5',
      cursor: 'pointer',
      fontSize: 13,
    },
    content: { padding: '24px 32px', maxWidth: 1400 },
    collapseBtn: {
      padding: '12px 16px',
      borderTop: '1px solid #1A2332',
      cursor: 'pointer',
      color: '#5A6175',
      fontSize: 13,
      textAlign: 'center',
    },
    adminInfo: {
      padding: '16px',
      borderTop: '1px solid #1A2332',
      display: sidebarOpen ? 'flex' : 'none',
      alignItems: 'center',
      gap: 10,
    },
    avatar: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${roleColors[admin?.role] || '#6B7280'}, #0057FF)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
    },
  };

  const initials = admin?.name
    ? admin.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <div style={s.wrapper}>
      <aside style={s.sidebar}>
        <div style={s.logo} onClick={() => navigate('/admin/dashboard')}>
          <span style={{ color: '#FFFFFF' }}>Talk</span>
          {sidebarOpen && <span style={{ color: '#0057FF' }}>Australia</span>}
          {sidebarOpen && <span style={{ fontSize: 11, color: '#5A6175', marginLeft: 4 }}>Admin</span>}
        </div>
        <nav style={s.nav}>
          {navItems.map((item) => {
            const isActive = currentPath === item.path || (item.path !== '/admin/dashboard' && currentPath.startsWith(item.path));
            return (
              <div
                key={item.path}
                style={s.navItem(isActive)}
                onClick={() => navigate(item.path)}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#1A2332'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={s.navIcon}>{item.icon}</span>
                <span style={s.navLabel}>{item.label}</span>
              </div>
            );
          })}
        </nav>
        {admin && (
          <div style={s.adminInfo}>
            <div style={s.avatar}>{initials}</div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{admin.name}</div>
              <div style={{ fontSize: 11, color: '#5A6175' }}>{admin.role}</div>
            </div>
          </div>
        )}
        <div style={s.collapseBtn} onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? '◀ Collapse' : '▶'}
        </div>
      </aside>

      <main style={s.main}>
        <header style={s.header}>
          <div style={s.headerTitle}>{title || 'Admin Portal'}</div>
          <div style={s.headerRight}>
            {admin && <span style={s.roleBadge}>{admin.role}</span>}
            {admin && <span style={{ color: '#5A6175', fontSize: 13 }}>{admin.email}</span>}
            <button style={s.logoutBtn} onClick={adminLogout}>Sign Out</button>
          </div>
        </header>
        <div style={s.content}>
          {children}
        </div>
      </main>
    </div>
  );
}
